import { useState, type ReactElement } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BoltIcon, TrashIcon } from '@heroicons/react/24/outline';
import {
  Alert,
  Badge,
  Button,
  Checkbox,
  DescriptionList,
  Modal,
  SelectField,
  SkeletonRows,
  TextAreaField,
  useToast,
} from '@csa/ui';
import { ApiError, api } from '../../lib/api.js';
import { SodFindings } from './SodFindings.js';
import { useTimeZone } from '../../lib/TimeZoneProvider.js';
import type { AssignmentView, RoleView, UserDetail } from './types.js';

/** Matches MAX_BREAKGLASS_HOURS on the server. */
const BREAKGLASS_HOURS = 8;

/**
 * One user's access: what was granted, by whom, why, and what it actually
 * amounts to once target scoping is applied.
 *
 * Granted and effective are shown separately because they are not the same
 * thing. A role can confer `query.execute.write` and still grant nothing if
 * it is scoped to no target, and an access review that reads only the role
 * names would record access that does not exist.
 */
export function UserDetailPanel({
  userId,
  onClose,
}: {
  userId: string;
  onClose: () => void;
}): ReactElement {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const tz = useTimeZone();
  const [error, setError] = useState<string | null>(null);

  const detail = useQuery({
    queryKey: ['admin', 'user', userId],
    queryFn: () => api.get<UserDetail>(`/api/admin/users/${userId}`),
  });

  const roles = useQuery({
    queryKey: ['admin', 'roles'],
    queryFn: () => api.get<{ roles: RoleView[] }>('/api/admin/roles'),
    staleTime: 5 * 60_000,
  });

  const [roleId, setRoleId] = useState('');
  const [justification, setJustification] = useState('');
  const [breakglass, setBreakglass] = useState(false);
  /**
   * The assignment awaiting confirmation. Revocation is a single click on a
   * small icon in a stacked list, and the rows differ only by role name, so
   * the confirmation exists to make the reader look at which one they picked
   * rather than to add ceremony.
   */
  const [confirming, setConfirming] = useState<AssignmentView | null>(null);

  async function refresh(): Promise<void> {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['admin', 'user', userId] }),
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] }),
      queryClient.invalidateQueries({ queryKey: ['admin', 'roles'] }),
    ]);
  }

  const assign = useMutation({
    mutationFn: () =>
      api.post(`/api/admin/users/${userId}/roles`, {
        roleId,
        source: breakglass ? 'breakglass' : 'direct',
        justification: justification.trim(),
        expiresAt: breakglass
          ? new Date(Date.now() + BREAKGLASS_HOURS * 3_600_000).toISOString()
          : null,
      }),
    onSuccess: async () => {
      await refresh();
      toast({ tone: 'success', title: 'Role assigned' });
      setRoleId('');
      setJustification('');
      setBreakglass(false);
      setError(null);
    },
    onError: (err: unknown) =>
      setError(err instanceof ApiError ? err.message : 'The role could not be assigned.'),
  });

  const revoke = useMutation({
    mutationFn: (assignment: AssignmentView) =>
      api.delete(
        `/api/admin/users/${userId}/roles?roleId=${assignment.roleId}&source=${assignment.source}`,
      ),
    onSuccess: async () => {
      await refresh();
      toast({
        tone: 'success',
        title: 'Role revoked',
        message: 'This takes effect on the user’s next request.',
      });
      setError(null);
    },
    onError: (err: unknown) =>
      setError(err instanceof ApiError ? err.message : 'The role could not be revoked.'),
    // Closed either way. A failure is reported in the panel behind this
    // dialog, which the dialog would otherwise be covering.
    onSettled: () => setConfirming(null),
  });

  const user = detail.data?.user;
  const assignedRoleIds = new Set((detail.data?.assignments ?? []).map((a) => a.roleId));
  const assignable = (roles.data?.roles ?? []).filter((r) => !assignedRoleIds.has(r.id));

  const canAssign = roleId !== '' && justification.trim().length >= 10 && !assign.isPending;

  return (
    <>
      <Modal
        open
        onClose={onClose}
        title={user ? user.displayName : 'User access'}
        description={user?.upn}
        size="lg"
        footer={<Button onClick={onClose}>Close</Button>}
      >
        {detail.isPending ? (
          <SkeletonRows rows={5} />
        ) : detail.error || !detail.data ? (
          <Alert tone="danger" title="Could not load this user">
            {detail.error?.message ?? 'No data returned.'}
          </Alert>
        ) : (
          <div className="flex flex-col gap-6">
            {error ? (
              <Alert tone="danger" title="Action failed">
                {error}
              </Alert>
            ) : null}

            {!detail.data.user.enabled ? (
              <Alert tone="danger" title="Account disabled">
                {detail.data.user.disabledReason ?? 'No reason recorded.'}
              </Alert>
            ) : null}

            <SodFindings analysis={detail.data.sod} context="user" />

            <DescriptionList
              items={[
                {
                  label: 'First signed in',
                  value: (
                    <span title={tz.titleFor(detail.data.user.firstSeenAt)}>
                      {tz.format(detail.data.user.firstSeenAt)}
                    </span>
                  ),
                },
                {
                  label: 'Last seen',
                  value: (
                    <span title={tz.titleFor(detail.data.user.lastSeenAt)}>
                      {tz.format(detail.data.user.lastSeenAt)}
                    </span>
                  ),
                },
              ]}
            />

            <section className="flex flex-col gap-3">
              <h3 className="text-sm font-semibold text-body">Assigned roles</h3>

              {detail.data.assignments.length === 0 ? (
                <p className="rounded-md border border-line bg-sunken px-3 py-2.5 text-sm text-muted">
                  No roles assigned. This user can sign in but cannot do anything.
                </p>
              ) : (
                <ul className="flex flex-col divide-y divide-line rounded-md border border-line">
                  {detail.data.assignments.map((assignment) => (
                    <li
                      key={`${assignment.roleId}:${assignment.source}`}
                      className="flex items-start justify-between gap-4 p-3"
                    >
                      <div className="min-w-0">
                        <p className="flex flex-wrap items-center gap-2 text-sm font-medium text-body">
                          {assignment.roleName}
                          <SourceBadge source={assignment.source} />
                          {assignment.expired ? <Badge tone="neutral">Expired</Badge> : null}
                        </p>
                        <p className="mt-1 text-xs text-muted">{assignment.justification}</p>
                        <p className="mt-1 text-xs text-subtle">
                          Granted {tz.format(assignment.grantedAt)}
                          {assignment.grantedBy ? ` by ${assignment.grantedBy}` : ''}
                          {assignment.expiresAt
                            ? ` · expires ${tz.format(assignment.expiresAt)}`
                            : ''}
                        </p>
                      </div>

                      <Button
                        variant="ghost"
                        size="xs"
                        iconOnly
                        aria-label={`Revoke ${assignment.roleName}`}
                        // Group-derived assignments are reconciled from Entra;
                        // deleting the row here would reinstate it at the next
                        // sync, so the server refuses and the button says so.
                        disabled={assignment.source === 'entra-group' || revoke.isPending}
                        title={
                          assignment.source === 'entra-group'
                            ? 'Remove the user from the Entra group instead'
                            : undefined
                        }
                        onClick={() => setConfirming(assignment)}
                      >
                        <TrashIcon className="size-4" aria-hidden="true" />
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section className="flex flex-col gap-3 rounded-md border border-line bg-sunken p-4">
              <h3 className="text-sm font-semibold text-body">Grant a role</h3>

              <SelectField
                label="Role"
                value={roleId}
                onChange={(e) => setRoleId(e.target.value)}
                options={[
                  { value: '', label: 'Select a role…' },
                  ...assignable.map((role) => ({
                    value: role.id,
                    label: `${role.name} (${role.permissions.length} permissions)`,
                  })),
                ]}
              />

              <TextAreaField
                label="Justification"
                description="Why does this person need this access? The access review reads this field, so reference the ticket or the change."
                value={justification}
                onChange={(e) => setJustification(e.target.value)}
                rows={2}
                required
              />

              <Checkbox
                checked={breakglass}
                onChange={(e) => setBreakglass(e.target.checked)}
                label={
                  <span className="flex items-center gap-1.5">
                    <BoltIcon className="size-4 text-brand" aria-hidden="true" />
                    Break-glass
                  </span>
                }
                description={`Emergency access that expires automatically after ${BREAKGLASS_HOURS} hours. Reported separately in the access review.`}
              />

              <div>
                <Button
                  variant="primary"
                  onClick={() => assign.mutate()}
                  disabled={!canAssign}
                  loading={assign.isPending}
                >
                  Grant role
                </Button>
              </div>
            </section>

            <section className="flex flex-col gap-3">
              <h3 className="text-sm font-semibold text-body">Effective access</h3>
              <p className="text-xs text-muted">
                What these roles actually permit once target scoping is applied.
              </p>

              {detail.data.effective.global.length > 0 ? (
                <div>
                  <p className="text-xs font-medium text-subtle">Everywhere</p>
                  <p className="mt-1 flex flex-wrap gap-1">
                    {detail.data.effective.global.map((permission) => (
                      <Badge key={permission} tone="neutral">
                        {permission}
                      </Badge>
                    ))}
                  </p>
                </div>
              ) : null}

              {detail.data.effective.byTarget.map((target) => (
                <div key={target.targetId}>
                  <p className="text-xs font-medium text-subtle">{target.targetKey}</p>
                  <p className="mt-1 flex flex-wrap gap-1">
                    {target.permissions.map((permission) => (
                      <Badge key={permission} tone="brand">
                        {permission}
                      </Badge>
                    ))}
                  </p>
                </div>
              ))}

              {detail.data.effective.all.length === 0 ? (
                <p className="text-sm text-muted">
                  Nothing. This account cannot perform any action.
                </p>
              ) : null}
            </section>
          </div>
        )}
      </Modal>

      {confirming ? (
        <Modal
          open
          onClose={() => setConfirming(null)}
          size="sm"
          title="Revoke this role?"
          /**
           * Escape and the backdrop both cancel, deliberately. For a
           * confirmation, an accidental dismissal is the harmless outcome --
           * blocking it would only take away the keyboard route out.
           */
          footer={
            <>
              <Button variant="ghost" onClick={() => setConfirming(null)}>
                Keep it
              </Button>
              <Button
                variant="danger"
                loading={revoke.isPending}
                onClick={() => revoke.mutate(confirming)}
              >
                Revoke
              </Button>
            </>
          }
        >
          <div className="flex flex-col gap-3 text-sm">
            <p className="text-body">
              <span className="font-medium">{confirming.roleName}</span> will be removed from{' '}
              <span className="font-medium">{user?.displayName ?? 'this user'}</span>
              {user ? <span className="text-muted"> ({user.upn})</span> : null}.
            </p>

            <p className="text-muted">
              It takes effect on their next request, and is recorded in the audit trail against your
              account. Granting it again needs a fresh justification.
            </p>

            {/*
              Shown because it is the reason the role was granted, and the
              person revoking it may not be the person who read that reason.
            */}
            {confirming.justification ? (
              <div className="rounded-md border border-line bg-sunken px-3 py-2">
                <p className="text-xs font-medium text-subtle">Granted for</p>
                <p className="mt-1 text-xs text-muted">{confirming.justification}</p>
              </div>
            ) : null}
          </div>
        </Modal>
      ) : null}
    </>
  );
}

function SourceBadge({ source }: { source: AssignmentView['source'] }): ReactElement {
  if (source === 'breakglass') return <Badge tone="danger">Break-glass</Badge>;
  if (source === 'entra-group') return <Badge tone="info">Entra group</Badge>;
  return <Badge tone="neutral">Direct</Badge>;
}
